import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Card, CardContent, IconButton } from '@mui/joy';
import { DotsSixVertical as DragIcon } from '@phosphor-icons/react';

interface DragDropListProps<T> {
  items: T[];
  onReorder: (items: T[]) => void;
  renderItem: (item: T, index: number, isDragging?: boolean) => React.ReactNode;
  keyExtractor: (item: T) => string;
  containerProps?: any;
  itemProps?: any;
}

interface SortableItemProps {
  id: string;
  children: React.ReactNode;
  itemProps?: any;
}

const SortableItem: React.FC<SortableItemProps> = ({ id, children, itemProps }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      sx={{
        position: 'relative',
        ...itemProps?.sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton
          {...listeners}
          size="sm"
          sx={{
            cursor: 'grab',
            '&:active': { cursor: 'grabbing' },
            mr: 1,
            color: 'neutral.500',
            '&:hover': {
              color: 'var(--brand-text, #7013f5)',
              bgcolor: 'transparent',
            },
          }}
        >
          <DragIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export function DragDropList<T>({
  items,
  onReorder,
  renderItem,
  keyExtractor,
  containerProps = {},
  itemProps = {},
}: DragDropListProps<T>) {
  const [activeId, setActiveId] = useState<string | null>(null);
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex(item => keyExtractor(item) === active.id);
      const newIndex = items.findIndex(item => keyExtractor(item) === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        onReorder(arrayMove(items, oldIndex, newIndex));
      }
    }

    setActiveId(null);
  };

  const activeItem = activeId ? items.find(item => keyExtractor(item) === activeId) : null;
  const activeIndex = activeItem ? items.indexOf(activeItem) : -1;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map(keyExtractor)}
        strategy={verticalListSortingStrategy}
      >
        <Box {...containerProps}>
          {items.map((item, index) => (
            <SortableItem
              key={keyExtractor(item)}
              id={keyExtractor(item)}
              itemProps={itemProps}
            >
              {renderItem(item, index)}
            </SortableItem>
          ))}
        </Box>
      </SortableContext>
      
      <DragOverlay>
        {activeItem ? (
          <Card
            variant="outlined"
            sx={{
              boxShadow: 'lg',
              bgcolor: 'background.surface',
              cursor: 'grabbing',
            }}
          >
            <CardContent>
              {renderItem(activeItem, activeIndex, true)}
            </CardContent>
          </Card>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
